import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { combineLatest, combineLatestWith, interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { SettingsService } from 'src/app/services/services/settings.service';
import { NGXLogger } from 'ngx-logger';
import { Store } from '@ngrx/store';
import * as mainActions from 'src/app/state/app.actions';
import { State } from 'src/app/dashboards/state';
import { realTimeService } from 'src/app/services/services/realtime.service';

type WindowRange = 'day' | 'week';

export interface Agent {
  id: string;
  name: string;
}
export interface AgentData {
  userName: string;
  userId: number;
  agents: Agent[];
}

export interface AgentQueueRow {
  queue: string;
  totalOffered: number;
  totalAnswered: number;
  avgSurveyScore?: number | null;
  formattedAvgSurveyScore?: string;
  answerRate: number | null;
  formattedAnswerRate: string;
  secondsAvgSpeak: number | null;
  formattedSecondsAvgSpeak: string;
  secondsAvgWrapup: number | null;
  formattedSecondsAvgWrapup: string;
  secondsAHT: number | null;
  formattedSecondsAHT: string;
}



interface Pause {
  id: number;
  description: string;
  timePaused: string; // e.g. "55:52" or "1d 12:16:32"
}

interface AgentSummary {
  secondsLoggedIn: number;
  secondsInPause: number;
  secondsAvgSpeak: number;
  secondsAvgWrapup: number;
  secondsAHT: number;
  totalOfferd: number;
  totalAnswered: number;
  avgSurveyScore: number | null;
  pauses: Pause[];
  formattedAvgSurveyScore: string;
  loggedIn: string; // e.g. "1d 16:31:54"
  inPause: string;  // e.g. "1d 13:12:24"
}


@Component({
    selector: 'app-ticker-agent',
    templateUrl: './ticker-agent.component.html',
    styleUrls: ['./ticker-agent.component.scss'],
    standalone: false
})
export class TickerAgentComponent implements OnInit, OnDestroy {
  // Route params
  userId: number | null = null;
  userGroupName: string = '';
  type: string = '';

  // Config / flags
  pollingIntervalMs = 10000;
  selectedWindow: WindowRange = 'day';
  showSurveyScore = true;

  // Agent data
  currentAgentData: AgentData | null = null;
  otherAgents: Agent[] = [];
  agentSummary: AgentSummary | null = null;
  totals: AgentQueueRow | null = null;

  // Table
  displayedColumns: string[] = [
    'queue',
    'totalOffered',
    'totalAnswered',
    'answerRate',
    'avgSpeak',
    'avgWrapup',
    'aht'
  ];
  displayedFooterColumns = [...this.displayedColumns];

  // Breadcrumbs
  BreadcrumbsLink = [];

  dataSource = new MatTableDataSource<AgentQueueRow>([]);
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  // UI state
  isLoading = true;
  errorMessage = '';

  // Polling
  private pollingSub?: Subscription;
  private routeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private realTimeService: realTimeService,
    private logger: NGXLogger,
    private store: Store<State>,
    private settingsService: SettingsService,
    private i18nextPipe: I18NextPipe,

  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;

    var settings = await this.settingsService.realtimeSettings();
    this.showSurveyScore = settings?.agentShowSurveyScore ?? false;
    this.pollingIntervalMs = (settings?.refreshInterval ?? 10) * 1000;
    // Add survey score column if enabled
    if (this.showSurveyScore && !this.displayedColumns.includes('avgSurveyScore')) {
      const idx = this.displayedColumns.indexOf('answerRate');
      this.displayedColumns.splice(idx, 0, 'avgSurveyScore');
      const fIdx = this.displayedFooterColumns.indexOf('answerRate');
      this.displayedFooterColumns.splice(fIdx, 0, 'avgSurveyScore');
    }

    // Setup sorting
    this.dataSource.sortingDataAccessor = (row: AgentQueueRow, column: string) => {
      switch (column) {
        case 'queue': return (row.queue || '').toLowerCase();
        case 'totalOffered': return row.totalOffered ?? -1;
        case 'totalAnswered': return row.totalAnswered ?? -1;
        case 'avgSurveyScore': return row.avgSurveyScore == null ? -1 : row.avgSurveyScore;
        case 'answerRate': return row.answerRate == null ? -1 : row.answerRate;
        case 'avgSpeak': return row.secondsAvgSpeak ?? -1;
        case 'avgWrapup': return row.secondsAvgWrapup ?? -1;
        case 'aht': return row.secondsAHT ?? -1;
        default: return '';
      }
    };
    this.dataSource.sort = this.sort;

    // Subscribe to route parameter changes
    // this.routeSub = this.route.paramMap.subscribe(params => {
    //   const userIdParam = params.get('userId');
    //   if (userIdParam) {
    //     this.userId = Number(userIdParam);
    //     this.loadData(true);
    //     this.startPolling();
    //   }
    // });

    this.routeSub = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap
    ]).subscribe(([params, queryParams]) => {
      const userIdParam = params.get('userId');
      const groupNameParam = queryParams.get('groupName');
      const typeParam = queryParams.get('type');

      if (userIdParam) {
        this.userId = Number(userIdParam);
        this.userGroupName = groupNameParam || '';
        this.type = typeParam || '';
        this.loadData(true);
        this.startPolling();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.routeSub?.unsubscribe();
  }

  // --- Data load (poll) ---
  private loadData(init: boolean = false): void {
    if (!this.userId || isNaN(Number(this.userId))) {
      this.errorMessage = this.i18nextPipe.transform('ticker:ticker_noAgentSelected', { defaultValue: 'No agent selected' });
      this.isLoading = false;
      return;
    }
    if (init) {
      if (init) {
        const allAgents$ = this.realTimeService.getTickerAgentsInfo(Number(this.userId));
        const agentInfo$ = this.realTimeService.getTickerAgentInfo(Number(this.userId), this.selectedWindow === 'day' ? 0 : 1);
        allAgents$.pipe(combineLatestWith(agentInfo$)).subscribe(([allAgents, agentInfo]) => {
          this.otherAgents = allAgents?.agents || [];
          this.currentAgentData = allAgents;
          this.userId = this.currentAgentData?.userId;
          this.isLoading = false;
          this.agentSummary = agentInfo?.time_info || null;
          this.dataSource.data = agentInfo?.queues?.rows || [];
          this.dataSource.data = this.dataSource.data.concat(agentInfo?.queues?.transfer || []);
          this.totals = agentInfo?.queues?.totals || null;
          this.BreadcrumbsLink = [
            { "label": "Home", "url": "/" },
            { "label": "Real-Time", "url": "/real-time/overview" },
            { "label": this.type === 'user-groups' ? 'User Groups' : 'Ticker User Groups', "url": `/real-time/${this.type}` },
            { "label": this.userGroupName?this.userGroupName:"All Agents", "url": `/real-time/${this.userGroupName?("ticker-user-groups/get/"+this.userGroupName):"user-groups/all-agents"}` },
            { "label": this.currentAgentData?.userName || '', "url": `/real-time/ticker-agents/details/${this.userId}` }];
        });
      }
    } else {
      this.realTimeService.getTickerAgentInfo(Number(this.userId), this.selectedWindow === 'day' ? 0 : 1).subscribe({
        next: (agentInfo) => {
          this.agentSummary = agentInfo?.time_info || null;
          this.dataSource.data = agentInfo?.queues?.rows || [];
          this.dataSource.data = this.dataSource.data.concat(agentInfo?.queues?.transfer || []);
          this.totals = agentInfo?.queues?.totals || null;
        },
        error: (error) => {
          this.errorMessage = this.i18nextPipe.transform('ticker:ticker_errorLoading', { defaultValue: 'Error loading agent data' });
        }
      });
    }
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => {
      this.loadData(false);
    });
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  // --- Toggle window ---
  onSelectWindow(range: WindowRange): void {
    if (this.selectedWindow === range) return;
    this.selectedWindow = range;
    this.loadData(true);
  }

  // --- Agent selection ---
  onAgentChange(agentId: number): void {
    // Navigate to the same page with the new agentId
    this.router.navigate(['/real-time/ticker-agents/details', agentId]);
  }

  // --- Help dialogs ---
  openAgentHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18nextPipe.transform('ticker:help_title_agent'),
        text: this.i18nextPipe.transform('ticker:help_agent'),
        renderAsHtml: false
      }
    });
  }

  openAgentStatusHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18nextPipe.transform('ticker:help_title_agentPauses'),
        text: this.i18nextPipe.transform('ticker:help_agentPauses'),
        renderAsHtml: true
      }
    });
  }
}
